import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { Button, Label } from '@zafroi/ui-kit';
import { uploadImage } from '@/services/uploads';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const ALLOWED = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Avatar/logo uploader for the chatbot widget header. Drag-drop or click,
 * with a circular preview (matching how the widget renders it), client-side
 * type/size guards, and the recommended dimensions + limits shown inline.
 * Uploads to S3 via the shared platform endpoint and returns a public URL.
 */
export function AvatarUpload({
  value,
  onChange,
  ownerId,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  ownerId?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const upload = useMutation({
    mutationFn: (file: File) => uploadImage({ file, scope: 'avatars', ownerId }),
    onSuccess: (res) => {
      onChange(res.url);
      toast.success('Image uploaded');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : 'Upload failed'),
  });

  const pick = (file: File | undefined | null) => {
    if (!file) return;
    if (!ALLOWED.has(file.type)) {
      toast.error('Use a PNG, JPG, WebP or SVG image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be under 5 MB.');
      return;
    }
    upload.mutate(file);
  };

  return (
    <div className="space-y-1.5">
      <Label>Avatar / logo</Label>
      <div
        className={`flex items-center gap-3 rounded-md border border-dashed p-3 transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-input'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0]);
        }}
      >
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-muted">
          {upload.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : value ? (
            <img src={value} alt="Avatar preview" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-muted-foreground">none</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" />
              {value ? 'Replace' : 'Upload image'}
            </Button>
            {value && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={upload.isPending}
                onClick={() => onChange(null)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            Square image works best — it&apos;s shown as a circle. PNG, JPG, WebP or SVG ·
            up to 5&nbsp;MB · <strong>256×256px recommended</strong> (min 64×64). Transparent
            PNG looks best. Leave empty to use your organization logo.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
